import { api_v1_playlists_path, api_v1_playlist_posts_path } from "../../routes"
import { mutationRequest } from "../request"
import { DirectUpload } from "@rails/activestorage"

export const uploadPlaylistImage = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const upload = new DirectUpload(
      new File([file], file.name.toLowerCase()),
      "/rails/active_storage/direct_uploads",
    )
    upload.create((error: Error, blob: { signed_id: string }) => {
      if (error) reject(error)
      else resolve(blob.signed_id)
    })
  })
}

interface PlaylistMutationParams {
  bandId?: number
  name: string
  description: string
  coverImageSignedId?: string
  isPublic?: boolean
}

export const createPlaylist = ({
  bandId,
  name,
  description,
  coverImageSignedId,
  isPublic = true,
}: PlaylistMutationParams) => {
  const playlistData: Record<string, unknown> = {
    band_id: bandId,
    name,
    description,
    is_public: isPublic,
  }
  if (coverImageSignedId) {
    playlistData.cover_image_signed_id = coverImageSignedId
  }
  return mutationRequest({
    path: api_v1_playlists_path(),
    options: { method: "POST" },
    data: { playlist: playlistData },
  })
}

export const addSongToPlaylist = (playlistId: number, postId: number) =>
  mutationRequest({
    path: api_v1_playlist_posts_path(),
    options: { method: "POST" },
    data: { playlist_post: { playlist_id: playlistId, post_id: postId } },
  })

export const removeFromPlaylist = async (playlistPostId: number) => {
  const token = localStorage.getItem("jwt_token")
  const csrfMeta = document.querySelector("meta[name=csrf-token]")
  const csrf = csrfMeta instanceof HTMLMetaElement ? csrfMeta.content : null
  const headers = new Headers()
  headers.set("Content-Type", "application/json")
  headers.set("Accept", "application/json")
  if (csrf) headers.set("X-CSRF-Token", csrf)
  if (token) headers.set("Authorization", `Bearer ${token}`)
  const response = await fetch(`/api/v1/playlist_posts/${playlistPostId}`, {
    method: "DELETE",
    headers,
    credentials: "same-origin",
  })
  if (response.status === 204 || response.ok) return { success: true }
  return { success: false }
}

export const followPlaylist = (playlistId: number) =>
  mutationRequest({
    path: `/api/v1/playlist_users`,
    options: { method: "POST" },
    data: { playlist_user: { playlist_id: playlistId } },
  })

export const unfollowPlaylist = (playlistUserId: number) =>
  mutationRequest({
    path: `/api/v1/playlist_users/${playlistUserId}`,
    options: { method: "DELETE" },
  })

export const getPlaylistUser = (playlistId: number) =>
  mutationRequest({
    path: `/api/v1/playlists/${playlistId}/playlist_users/current_user`,
    options: { method: "GET" },
  })

export const reorderPlaylistPost = (playlistPostId: number, trackNumber: number) =>
  mutationRequest({
    path: `/api/v1/playlist_posts/${playlistPostId}`,
    options: { method: "PUT" },
    data: { playlist_post: { track_number: trackNumber } },
  })

export const deletePlaylist = async (playlistId: number) => {
  const token = localStorage.getItem("jwt_token")
  const csrfMeta = document.querySelector("meta[name=csrf-token]")
  const csrf = csrfMeta instanceof HTMLMetaElement ? csrfMeta.content : null
  const headers = new Headers()
  headers.set("Content-Type", "application/json")
  headers.set("Accept", "application/json")
  if (csrf) headers.set("X-CSRF-Token", csrf)
  if (token) headers.set("Authorization", `Bearer ${token}`)
  const response = await fetch(`/api/v1/playlists/${playlistId}`, {
    method: "DELETE",
    headers,
    credentials: "same-origin",
  })
  if (response.status === 204 || response.ok) return { success: true }
  const body = await response.json().catch(() => ({}))
  return { success: false, error: body?.error || "Failed to delete playlist." }
}

export const updatePlaylist = (
  playlistId: number,
  { name, description, coverImageSignedId, removeCustomArtwork, isPublic }: PlaylistMutationParams & { removeCustomArtwork?: boolean },
) => {
  const playlistData: Record<string, unknown> = { name, description, is_public: isPublic ?? true }
  if (coverImageSignedId) {
    playlistData.cover_image_signed_id = coverImageSignedId
  }
  if (removeCustomArtwork) {
    playlistData.remove_custom_artwork = true
  }
  return mutationRequest({
    path: `/api/v1/playlists/${playlistId}`,
    options: { method: "PUT" },
    data: { playlist: playlistData },
  })
}
